"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { SearchIcon, SparkleIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

// Measure before paint on the client; fall back to useEffect during SSR so the
// toggle never emits the useLayoutEffect hydration warning.
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

/**
 * Segmented [Search | Ask AI] control for the explorer search field (AHD-8234).
 * The active segment is a pill that slides between the two options; the track
 * cross-fades between a light neutral surface (Ask-AI mode) and the brand
 * violet→periwinkle AI gradient (Search mode) — matching the Figma. Both the
 * pill slide and the colour→gradient morph are animated.
 *
 * The light state mirrors the hero tab group (neutral-100 track, white pill,
 * neutral-500 label) so the two segmented controls read as one family.
 *
 * The two segments share one grid column width, so the sliding pill keeps a
 * constant size as it moves — it never resizes between the (differently sized)
 * "Search" and "Ask AI" labels, which read as a jump.
 */
export function SearchModeToggle({
  aiMode,
  onSearch,
  onAskAi,
  searchLabel,
  askAiLabel,
  className,
}: {
  aiMode: boolean
  onSearch: () => void
  onAskAi: () => void
  searchLabel: string
  askAiLabel: string
  className?: string
}) {
  const searchRef = useRef<HTMLButtonElement>(null)
  const askRef = useRef<HTMLButtonElement>(null)
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null)

  useIsoLayoutEffect(() => {
    const measure = () => {
      const el = aiMode ? askRef.current : searchRef.current
      if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth })
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [aiMode, searchLabel, askAiLabel])

  return (
    <div
      className={cn(
        "relative grid shrink-0 grid-cols-2 rounded-full bg-neutral-100 p-0.5",
        className
      )}
    >
      {/* AI gradient surface — cross-fades in for Search mode (per Figma).
          Faster than the pill slide (200ms vs 300ms) so the track settles to
          its new colour before the pill arrives, not after it. */}
      <span
        aria-hidden="true"
        className="ai-gradient pointer-events-none absolute inset-0 rounded-full transition-opacity duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ opacity: aiMode ? 0 : 1 }}
      />
      {/* Sliding pill behind the active segment — white for Search, filled with
          the deeper violet pill gradient for Ask AI (its own ramp, not the
          track's). */}
      {pill && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute top-0.5 bottom-0.5 rounded-full shadow-sm transition-[left,width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            aiMode ? "ai-gradient-pill" : "bg-white"
          )}
          style={{ left: pill.left, width: pill.width }}
        />
      )}
      <button
        ref={searchRef}
        type="button"
        onClick={onSearch}
        aria-pressed={!aiMode}
        className={cn(
          "type-title-sm relative z-10 flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 transition-colors duration-300",
          // Search active → dark on its white pill; inactive → muted on the
          // light track (the gradient track only exists in Search mode).
          !aiMode ? "text-neutral-900" : "text-neutral-500"
        )}
      >
        <SearchIcon width={15} height={15} />
        {searchLabel}
      </button>
      <button
        ref={askRef}
        type="button"
        onClick={onAskAi}
        aria-pressed={aiMode}
        className={cn(
          "type-title-sm relative z-10 flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-white transition-colors duration-300"
        )}
      >
        <SparkleIcon width={15} height={15} />
        {askAiLabel}
      </button>
    </div>
  )
}
